import type { TicketMessage } from "@/modules/types";
import { stripTrailingDuplicateOfQuoted } from "@/modules/tickets/dedupeQuotedEmailDisplay";
import { resolveTicketHtmlDisplay } from "@/modules/tickets/resolveTicketHtmlDisplay";
import {
  hasSubstantialQuotedContent,
  splitPlainTextEmail,
} from "@/modules/tickets/ticketEmailQuotedContent";
import { htmlToPlainText } from "@/modules/tickets/ticketReplyRichText";

const DEFAULT_PREVIEW_MAX_LENGTH = 120;

/**
 * Text saved before HTML-only emails were cleaned up starts with CSS rules
 * ("html, body { margin: 0; … }"): read the HTML instead.
 */
export const looksLikeCssText = (value: string) =>
  /^\s*(?:@[a-z-]+[^{]*|[a-z0-9_.#:*,\s\-[\]="'>()]+)\{[^}]*:[^}]*;/i.test(
    value.slice(0, 400),
  );

/** One-line preview from main content only — strips nested quoted history. */
export const ticketMessagePreviewText = (
  message: Pick<TicketMessage, "body" | "html_body" | "attachments">,
  maxLength = DEFAULT_PREVIEW_MAX_LENGTH,
): string | null => {
  const html = message.html_body?.trim();
  const saved = message.body?.trim();
  const plain = html && saved && looksLikeCssText(saved) ? undefined : saved;

  let main = "";

  if (html) {
    const display = resolveTicketHtmlDisplay(html);
    if (display.mode === "split") {
      main = htmlToPlainText(display.content);
    } else if (plain) {
      const split = splitPlainTextEmail(plain);
      main =
        split.quoted && hasSubstantialQuotedContent(split.quoted)
          ? stripTrailingDuplicateOfQuoted(split.content, split.quoted, false)
          : split.content || plain;
    } else {
      main = htmlToPlainText(display.html);
    }
  } else if (plain) {
    const split = splitPlainTextEmail(plain);
    main =
      split.quoted && hasSubstantialQuotedContent(split.quoted)
        ? stripTrailingDuplicateOfQuoted(split.content, split.quoted, false)
        : split.content || plain;
  }

  const text = main.replace(/\s+/g, " ").trim();
  if (!text) {
    if (Array.isArray(message.attachments) && message.attachments.length > 0) {
      return "Attachment";
    }
    return null;
  }
  if (text.length <= maxLength) return text;
  return `${text.slice(0, maxLength).trimEnd()}…`;
};
